import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_PROCUREMENT_API ?? '/api/v1';

export type PurchaseRequisition = {
  id: string;
  pr_number: string;
  status: string;
  priority: string;
  total_amount: number;
};

export type PurchaseOrder = {
  id: string;
  po_number: string;
  status: string;
  vendor_id: string;
  total_amount: number;
};

export type ProcurementDashboard = {
  total_requisitions: number;
  pending_approvals: number;
  open_pos: number;
  open_rfqs: number;
  procurement_health: number;
  budget_consumed: number;
  budget_allocated: number;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getScopeHeaders(),
      ...init?.headers,
    },
  });
  if (!res.ok) throw new Error(await res.text() || res.statusText);
  if (res.status === 204) return undefined as T;
  const json = await res.json();
  return (json.data ?? json) as T;
}

export const procurementApi = {
  dashboard: () => request<ProcurementDashboard>('/procurement/dashboard'),

  listRequisitions: () => request<PurchaseRequisition[]>('/procurement/requisitions'),

  createRequisition: (body: { priority?: string; items: { sku: string; quantity: number; unit_price: number }[] }) =>
    request<PurchaseRequisition>('/procurement/requisitions', { method: 'POST', body: JSON.stringify(body) }),

  createRFQ: (body: { title: string; vendor_ids: string[]; deadline: string; items: { sku: string; quantity: number; unit_price: number }[] }) =>
    request<unknown>('/procurement/rfq', { method: 'POST', body: JSON.stringify(body) }),

  listRFQs: () => request<unknown[]>('/procurement/rfq'),

  createPO: (body: { vendor_id: string; items: { sku: string; quantity: number; unit_price: number }[] }) =>
    request<PurchaseOrder>('/procurement/purchase-orders', { method: 'POST', body: JSON.stringify(body) }),

  listPOs: () => request<PurchaseOrder[]>('/procurement/purchase-orders'),

  createGRN: (body: { po_id: string; items: { sku: string; received: number; accepted: number; damaged: number }[] }) =>
    request<unknown>('/procurement/grn', { method: 'POST', body: JSON.stringify(body) }),

  runMRP: (skus: { sku: string; on_hand: number; demand_forecast: number; safety_stock: number; lead_time_days: number }[]) =>
    request<unknown[]>('/procurement/mrp/run', { method: 'POST', body: JSON.stringify({ skus }) }),

  analytics: () => request<{ total_spend: number; avg_lead_time_days: number; budget_variance: number }>('/procurement/analytics'),

  intelligence: () => request<{ health_score: number; recommendations: string[] }>('/procurement/intelligence'),
};
